import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Button, Text } from "north.js";
import React from "react";
import styled from "styled-components";
import { NumberParam, useQueryParams } from "use-query-params";

import { Header, Link, PageWrapper, Spinner } from "src/shared/components";
import { Route } from "src/shared/constants/route";
import { ProfileContextProvider } from "src/shared/contexts/ProfileContext";
import { deviceBreakpoints } from "src/theme/deviceBreakpoints";
import { useGetDiscoveryProfileByClaimIdQuery } from "src/views/hacker-directory/graphql/getDiscoveryProfileByClaimId.generated";
import {
  ProfileDisplayDetails,
  ProfileDisplayIdentity,
  ProfileDisplayLayout,
} from "src/views/hacker-directory/shared/ProfileDisplay";
import { mapDiscoveryProfile } from "src/views/hacker-directory/shared/utils";

const HackerDirectoryProfile: React.FC = () => {
  const [{ ref: profileRef }] = useQueryParams({ ref: NumberParam });
  const validProfileRef =
    typeof profileRef === "number" &&
    Number.isInteger(profileRef) &&
    profileRef > 0
      ? profileRef
      : null;
  const { data, loading, error, refetch } =
    useGetDiscoveryProfileByClaimIdQuery({
      variables: { claimId: validProfileRef ?? 0 },
      skip: validProfileRef == null,
    });
  const profileResult = data?.profiles[0];
  const profile =
    profileResult && validProfileRef != null
      ? mapDiscoveryProfile(validProfileRef, profileResult.fields)
      : null;

  return (
    <>
      <Header
        title="Hacker Directory"
        subtitle="Looking for teammates? Browse for hackers that match your skills and interests!"
      />
      <ProfilePageWrapper>
        <BackLink href={Route.HACKER_DIRECTORY_DIRECTORY}>
          <ArrowLeftIcon aria-hidden="true" />
          Back to directory
        </BackLink>

        {loading && <Spinner text="Loading hacker profile..." />}

        {!loading && profile && (
          <ProfileDisplayLayout
            breakpoints={{
              details: {
                stackedLayout: 1168,
                compactDetails: 700,
              },
              identity: {
                sideBySide: [
                  {
                    minWidth: 540,
                    maxWidth: deviceBreakpoints.tablet + 1,
                  },
                  {
                    minWidth: 968,
                    maxWidth: 1168,
                  },
                ],
              },
            }}
          >
            <ProfileDisplayDetails profile={profile} />
            <ProfileDisplayIdentity profile={profile} connectSlack />
          </ProfileDisplayLayout>
        )}

        {!loading && !profile && (
          <ProfileState role="alert">
            <Text type="h3">Profile unavailable</Text>
            <SecondaryText type="body1">
              {error
                ? "We couldn’t load this hacker’s profile. Please try again."
                : "We couldn’t find a directory profile for this hacker."}
            </SecondaryText>
            {error && validProfileRef != null && (
              <Button color="primary" size="md" onClick={() => void refetch()}>
                Try again
              </Button>
            )}
          </ProfileState>
        )}
      </ProfilePageWrapper>
    </>
  );
};

const ProfilePageWrapper = styled(PageWrapper)`
  padding-bottom: 64px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  margin-bottom: 40px;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ProfileState = styled.div`
  display: flex;
  min-height: 360px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
`;

const SecondaryText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

export default () => (
  <ProfileContextProvider>
    <HackerDirectoryProfile />
  </ProfileContextProvider>
);
